import { PDFViewer } from '@react-pdf/renderer';
import { useFormContext } from 'react-hook-form';
import { RetailInvoicePDF } from 'src/components/RetailInvoice';
import { BuyerCard } from 'src/components/Selectors/BuyerSelector/BuyerCard';
import { useAppContext } from 'src/contexts/AppContext';
import { useGetBuyerPaymentMethods } from 'src/hooks/api/useGetBuyerPaymentMethods';
import {
  BuyerWithSalesDto,
  EventWithData,
  ListingDetails,
  MarketplacePaymentLineType,
  MarketplacePaymentState,
} from 'src/WebApiController';

import { useRetailSaleContext } from '../RetailSaleLocalContext';
import { RetailSaleFormValues } from '../types';
import * as styles from './RetailSaleInvoicePreviewStep.css';

type RetailSaleInvoicePreviewStepProps = {
  listing: ListingDetails | undefined;
  event: EventWithData | undefined;
};

export const RetailSaleInvoicePreviewStep = ({
  listing,
  event,
}: RetailSaleInvoicePreviewStepProps) => {
  const { loginContext } = useAppContext();
  const { watch } = useFormContext<RetailSaleFormValues>();
  const values = watch();

  const { selectedStoredPaymentMethodId } = useRetailSaleContext();
  const { data: savedPaymentMethods } = useGetBuyerPaymentMethods(
    values.buyerEmail
  );

  const selectedPaymentMethod = savedPaymentMethods?.find(
    (pm) => pm.storedPaymentMethodId === selectedStoredPaymentMethodId
  );
  const paymentMethodDisplay = selectedPaymentMethod
    ? `${selectedPaymentMethod.cardBrand ?? ''} ****${selectedPaymentMethod.cardSummary ?? ''}`.trim()
    : undefined;

  const proceeds = values.totalNetProceeds ?? 0;
  const marketplaceLinesCost =
    values.marketplacePaymentLines?.reduce(
      (sum, item) => sum + (item.amount ?? 0),
      0
    ) ?? 0;
  const totalAmount = proceeds + marketplaceLinesCost;

  const paymentsMade = (values.marketplacePaymentLines ?? [])
    .filter(
      (line) =>
        line.paymentLineType === MarketplacePaymentLineType.Proceeds &&
        line.paymentState === MarketplacePaymentState.Paid
    )
    .reduce((sum, line) => sum + (line.amount ?? 0), 0);
  const balanceDue = totalAmount - paymentsMade;

  const invoiceNumber =
    values.externalSaleId ?? `${listing?.id ?? values.listingId}`;

  const buyerAddress = [
    values.shippingStreet,
    values.shippingCity,
    values.shippingState,
    values.shippingCountry,
  ]
    .filter(Boolean)
    .join(', ');

  const pdfProps = {
    brokerName: loginContext?.user?.activeAccount?.accountName ?? '',
    brokerEmail: loginContext?.user?.userName ?? undefined,
    invoiceNumber,
    invoiceDate: values.saleDate ?? new Date().toISOString(),
    currency: values.currencyCode ?? 'USD',
    paymentTermsDays: values.paymentTermsDays,
    lineItems: [
      ...(proceeds !== 0
        ? [
            {
              description: 'Ticket Proceeds',
              amount: proceeds,
              costType: 'Proceeds' as const,
            },
          ]
        : []),
      ...(values.marketplacePaymentLines ?? []).map((line) => ({
        description: line.description ?? '',
        ratePercent: line.ratePercent ?? undefined,
        amount: line.amount ?? 0,
        costType: ((line.amount ?? 0) < 0 ? 'Credit' : 'Charge') as string,
      })),
    ],
    buyerName: values.buyerName,
    buyerEmail: values.buyerEmail,
    buyerAddress: buyerAddress || undefined,
    paymentMethod: paymentMethodDisplay,
    brokerNotes: values.privateNotes,
    totalAmount,
    balanceDue,
    paymentsMade,
    status: (values.paymentReceived === true ? 'Paid' : 'Outstanding') as
      | 'Paid'
      | 'Outstanding',
  };

  const eventName = event?.event?.name;
  const eventDate = event?.event?.dates?.start;
  const section = listing?.seating?.section ?? values.section;
  const row = listing?.seating?.row ?? values.row;
  const seatFr = listing?.seating?.seatFr ?? values.seatFrom;
  const seatTo = listing?.seating?.seatTo ?? values.seatTo;
  const ticketCount = listing?.availQty ?? values.quantitySold;

  const seatingDisplay = [
    section ? `Section ${section}` : null,
    row ? `Row ${row}` : null,
    seatFr && seatTo
      ? `Seats ${seatFr}–${seatTo}`
      : seatFr
        ? `Seat ${seatFr}`
        : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const buyerForCard: BuyerWithSalesDto = {
    buyerEmail: values.buyerEmail ?? '',
    buyerName: values.buyerName ?? null,
    buyerPhoneNumber: values.buyerPhone ?? null,
    internalNotes: null,
    lastRetailActivityDate: null,
    billingAddressLine1: values.billingStreet ?? null,
    billingAddressLine2: null,
    billingCity: values.billingCity ?? values.shippingCity ?? null,
    billingState: values.billingState ?? values.shippingState ?? null,
    billingZipCode: values.billingZipCode ?? values.shippingZipCode ?? null,
    billingCountry: values.billingCountry ?? values.shippingCountry ?? null,
    shippingAddressLine1: values.shippingStreet ?? null,
    shippingAddressLine2: null,
    shippingCity: values.shippingCity ?? null,
    shippingState: values.shippingState ?? null,
    shippingZipCode: values.shippingZipCode ?? null,
    shippingCountry: values.shippingCountry ?? null,
    cardBrand: selectedPaymentMethod?.cardBrand ?? null,
    cardSummary: selectedPaymentMethod?.cardSummary ?? null,
    expiryMonth: selectedPaymentMethod?.expiryMonth ?? null,
    expiryYear: selectedPaymentMethod?.expiryYear ?? null,
    saleIds: null,
  };

  return (
    <div className={styles.previewContainer}>
      {/* Customer Info Card */}
      {(values.buyerName || values.buyerEmail) && (
        <BuyerCard buyer={buyerForCard} />
      )}

      {/* PDF Viewer */}
      <div className={styles.pdfViewerContainer}>
        <PDFViewer width="100%" height="500px" showToolbar={false}>
          <RetailInvoicePDF {...pdfProps} />
        </PDFViewer>
      </div>
    </div>
  );
};
